import { type ApiEndpoint, type ApiEndpointParameter, type HttpMethod } from './portal-types';
import { type OpenApiDocument, type OpenApiOperation, type OpenApiSchema } from './openapi-types';
import { ENDPOINT_OVERLAYS } from './endpoint-overlays';

const HTTP_METHODS: readonly string[] = ['get', 'post', 'put', 'patch', 'delete'];

// Public API surface only. Backend specs can also expose internal routes (e.g.
// /api/integrations/... webhook receivers) that must never appear on the public portal.
const PUBLIC_PATH_PATTERN = /^\/api\/v\d+\/([^/]+)/;

// Controller segment in the path → display group name; unknown controllers fall back to the raw
// segment so new backend controllers still get a nav group without a frontend change.
const GROUP_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  User: 'Users',
  Lane: 'Lanes',
  Request: 'Requests',
  Order: 'Orders',
  Webhook: 'Webhooks',
};

const PREFERRED_GROUP_ORDER: readonly string[] = [
  'Users',
  'Lanes',
  'Requests',
  'Orders',
  'Webhooks',
];

/**
 * Parses an OpenAPI document into the endpoint view models rendered by the API reference,
 * merging in the hand-written overlays where available.
 */
export function parseOpenApiDocument(document: OpenApiDocument): ApiEndpoint[] {
  const endpoints: ApiEndpoint[] = [];
  for (const [path, operations] of Object.entries(document.paths)) {
    if (!PUBLIC_PATH_PATTERN.test(path)) {
      continue;
    }
    for (const [method, operation] of Object.entries(operations)) {
      if (!HTTP_METHODS.includes(method)) {
        continue;
      }
      endpoints.push(buildEndpoint(document, path, method.toUpperCase() as HttpMethod, operation));
    }
  }
  return sortByGroupOrder(endpoints);
}

/**
 * Returns the group names in display order for a parsed endpoint list.
 */
export function endpointGroupNames(endpoints: readonly ApiEndpoint[]): string[] {
  return [...new Set(endpoints.map((endpoint) => endpoint.group))];
}

function buildEndpoint(
  document: OpenApiDocument,
  path: string,
  method: HttpMethod,
  operation: OpenApiOperation,
): ApiEndpoint {
  const overlay = ENDPOINT_OVERLAYS[`${method} ${path}`];
  const bodySchema = resolveSchema(document, requestBodySchema(operation));
  const pathAndQueryParameters = (operation.parameters ?? []).filter(
    (parameter) => parameter.in === 'path' || parameter.in === 'query',
  );

  const parameters: ApiEndpointParameter[] = pathAndQueryParameters.map((parameter) => ({
    name: parameter.name,
    type: schemaTypeLabel(resolveSchema(document, parameter.schema)),
    required: parameter.required ?? false,
    description: parameter.description ?? '',
  }));
  for (const [name, property] of Object.entries(bodySchema?.properties ?? {})) {
    parameters.push({
      name,
      type: schemaTypeLabel(resolveSchema(document, property)),
      required: (bodySchema?.required ?? []).includes(name),
      description: property.description ?? '',
    });
  }

  const responseSchema = resolveSchema(document, successResponseSchema(operation));
  const requestBody = bodySchema
    ? (overlay?.requestBodyExample ?? buildExampleObject(document, bodySchema))
    : null;
  const response = responseSchema
    ? (overlay?.responseExample ?? buildExampleObject(document, responseSchema))
    : null;

  const endpoint: ApiEndpoint = {
    id: overlay?.id ?? autoEndpointId(method, path),
    group: groupForPath(path),
    method,
    path,
    examplePath: overlay?.examplePath ?? path,
    title: overlay?.title ?? operation.summary ?? path,
    description: overlay?.description ?? operation.description ?? operation.summary ?? '',
    parametersTitle: parametersTitle(pathAndQueryParameters, bodySchema),
    parameters,
    requestBody,
    response,
    returns: overlay?.returns ?? responseSchema?.description ?? '',
  };
  return endpoint;
}

function groupForPath(path: string): string {
  const controller = PUBLIC_PATH_PATTERN.exec(path)?.[1] ?? 'Other';
  return GROUP_DISPLAY_NAMES[controller] ?? controller;
}

function autoEndpointId(method: HttpMethod, path: string): string {
  const slug = path
    .replaceAll(/[{}]/g, '')
    .replaceAll(/[^a-zA-Z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '')
    .toLowerCase();
  return `${method.toLowerCase()}-${slug}`;
}

function parametersTitle(
  pathAndQueryParameters: readonly { in: string }[],
  bodySchema: OpenApiSchema | null,
): string {
  const hasPath = pathAndQueryParameters.some((parameter) => parameter.in === 'path');
  const hasQuery = pathAndQueryParameters.some((parameter) => parameter.in === 'query');
  const kinds = [hasPath, hasQuery, !!bodySchema].filter(Boolean).length;
  if (kinds > 1) {
    return 'Parameters';
  }
  if (bodySchema) {
    return 'Body parameters';
  }
  if (hasPath) {
    return 'Path parameters';
  }
  if (hasQuery) {
    return 'Query parameters';
  }
  return 'Parameters';
}

function requestBodySchema(operation: OpenApiOperation): OpenApiSchema | undefined {
  return operation.requestBody?.content?.['application/json']?.schema;
}

function successResponseSchema(operation: OpenApiOperation): OpenApiSchema | undefined {
  const responses = operation.responses ?? {};
  const success = responses['200'] ?? responses['201'];
  return success?.content?.['application/json']?.schema;
}

function resolveSchema(
  document: OpenApiDocument,
  schema: OpenApiSchema | undefined,
): OpenApiSchema | null {
  if (!schema) {
    return null;
  }
  const ref = schema.$ref ?? schema.allOf?.[0]?.$ref;
  if (!ref) {
    return schema;
  }
  const resolved = document.components?.schemas?.[ref.split('/').pop() ?? ''] ?? null;
  if (!resolved) {
    return null;
  }
  // Keep the property-level description (e.g. on an allOf wrapper) over the schema's own.
  if (schema.description && !resolved.description) {
    return { ...resolved, description: schema.description };
  }
  return resolved;
}

function schemaTypeLabel(schema: OpenApiSchema | null): string {
  if (!schema) {
    return 'object';
  }
  if (schema.properties || schema.allOf) {
    return 'object';
  }
  const type = schema.type ?? 'object';
  if (type === 'array') {
    return 'array';
  }
  if (schema.format && (schema.format === 'uuid' || schema.format === 'date-time')) {
    return `${type} (${schema.format})`;
  }
  return type;
}

function buildExampleObject(document: OpenApiDocument, schema: OpenApiSchema): object | null {
  const example = buildExample(document, schema);
  return typeof example === 'object' ? example : null;
}

// Fallback example values for endpoints without a curated overlay example.
function buildExample(document: OpenApiDocument, schema: OpenApiSchema | null, depth = 0): unknown {
  if (!schema || depth > 4) {
    return null;
  }
  const resolved = resolveSchema(document, schema);
  if (!resolved) {
    return null;
  }
  if (resolved.properties) {
    const example: Record<string, unknown> = {};
    for (const [name, property] of Object.entries(resolved.properties)) {
      example[name] = buildExample(document, property, depth + 1);
    }
    return example;
  }
  switch (resolved.type) {
    case 'array':
      return [buildExample(document, resolved.items ?? null, depth + 1)];
    case 'boolean':
      return true;
    case 'integer':
    case 'number':
      return typeof resolved.enum?.[0] === 'number' ? resolved.enum[0] : 0;
    case 'string':
      if (typeof resolved.enum?.[0] === 'string') {
        return resolved.enum[0];
      }
      if (resolved.format === 'uuid') {
        return '00000000-0000-0000-0000-000000000000';
      }
      if (resolved.format === 'date-time') {
        return '2026-01-01T00:00:00Z';
      }
      return 'string';
    default:
      return {};
  }
}

function sortByGroupOrder(endpoints: readonly ApiEndpoint[]): ApiEndpoint[] {
  const order = (group: string) => {
    const index = PREFERRED_GROUP_ORDER.indexOf(group);
    return index === -1 ? PREFERRED_GROUP_ORDER.length : index;
  };
  // Stable sort: groups follow the preferred order, endpoints keep spec order within a group.
  return [...endpoints].sort((a, b) => order(a.group) - order(b.group));
}
